import { useState } from 'react';
import { seedPricing, type PricingData } from '@rpl/pricing';
import { useStore } from '../../store/store';
import { CartModal } from './CartModal';

/**
 * "Add Instructions" dialog (cart page). Free-text note + quick-add chips for
 * the common asks; the copy makes the audience explicit (print technicians).
 * Config (chips + char cap) comes from pricing.orderNotes so it's
 * admin-editable via the pricing editor; the bundled seed is the fallback for
 * older published pricing versions. The note rides the Shopify cart note into
 * the order webhook (order.note) → job bag + admin dash + customer card.
 */
export function OrderNotesDialog({
  pricing,
  onClose,
}: {
  pricing: PricingData;
  onClose: () => void;
}) {
  const orderNote = useStore((s) => s.orderNote);
  const setOrderNote = useStore((s) => s.setOrderNote);
  const cfg = pricing.orderNotes ?? seedPricing.orderNotes ?? { quickAdd: [], maxChars: 500 };
  const [draft, setDraft] = useState(orderNote);

  const clamp = (s: string) => s.slice(0, cfg.maxChars);
  const addQuick = (chip: string) => {
    const has = draft.toLowerCase().includes(chip.toLowerCase());
    if (has) return;
    setDraft((d) => clamp(d.trim() ? `${d.trim()}\n${chip}` : chip));
  };

  const save = () => {
    setOrderNote(draft.trim());
    onClose();
  };

  return (
    <CartModal
      title="Instructions for our print technicians"
      caption="This will be shown to the technicians printing your order."
      onClose={onClose}
    >
      <div className="onotes">
        {cfg.quickAdd.length > 0 && (
          <div className="onotes__chips" aria-label="Quick add">
            {cfg.quickAdd.map((chip) => (
              <button
                type="button"
                key={chip}
                className="pill-btn onotes__chip"
                disabled={draft.toLowerCase().includes(chip.toLowerCase())}
                onClick={() => addQuick(chip)}
              >
                ＋ {chip}
              </button>
            ))}
          </div>
        )}
        <textarea
          className="onotes__text"
          value={draft}
          onChange={(e) => setDraft(clamp(e.target.value))}
          rows={5}
          maxLength={cfg.maxChars}
          placeholder="Anything we should know about printing your order…"
          aria-label="Instructions for the lab"
        />
        <div className="onotes__count" aria-live="polite">
          {draft.length}/{cfg.maxChars}
        </div>
        <div className="onotes__actions">
          {orderNote && (
            <button
              type="button"
              className="linkbtn upload__delete onotes__clear"
              onClick={() => {
                setOrderNote('');
                onClose();
              }}
            >
              Remove instructions
            </button>
          )}
          <button type="button" className="btn btn--secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn--primary" onClick={save}>
            Save instructions
          </button>
        </div>
      </div>
    </CartModal>
  );
}
